import { useState, useEffect } from 'react';
import useActions from 'hooks/useActions';
import { countriesSelector, statesOrProvincesSelector } from 'selectors/locationSelectors';
import { isFetchingEndpointSelector } from 'selectors/fetchingSelectors';
import * as locationActions from 'actions/location';
import { PROFILE } from 'constants';
import { useSelector } from 'react-redux';

const {
  FIELD_STATUS_MANDATORY,
  FIELD_STATUS_OPTIONAL,
  ISSUING_AUTHORITY_FIELD_COUNTRIES,
  ISSUING_AUTHORITIES_BY_COUNTRY,
} = PROFILE;

/**
 * This effect fires once when the component first renders (a.k.a. mounts).
 * It should get country data if its' not yet available, and set the selected country
 * object to the pre-selected country code (usually defaults to domain).
 */
const useLoadCountriesOnMount = (countries, getCountries, loadingOverlay) => {
  const isFetchingGetCountriesSortedByName = useSelector((state) =>
    isFetchingEndpointSelector(state, {
      endpoint: `location/countries-sorted-by-name`,
    })
  );

  useEffect(() => {
    if (!countries?.length && !isFetchingGetCountriesSortedByName) {
      getCountries(loadingOverlay);
    }
  }, []);
};

/**
 * Since initial country code might also depend on async actions, this effect fires
 * once both initial country code and countries data are available to select the
 * initial country data.
 */
const usePreSelectInitialCountry = (initialCountryCode, selectedCountry, setSelectedCountry, countries) => {
  useEffect(() => {
    if (!selectedCountry && initialCountryCode && countries?.length) {
      const countryObject = countries.find(({ country_code }) => country_code === initialCountryCode);
      setSelectedCountry(countryObject);
    }
  }, [initialCountryCode, countries?.length]);
};

/**
 * This effect runs everytime the country changes to make sure there is subdivision data when applicable.
 */
const useLoadSubdivisionsOnCountrySelected = (
  selectedCountry,
  stateData,
  getStatesOrProvinces,
  donotFetchStateAndProvinces
) => {
  useEffect(() => {
    const newCountryCode = selectedCountry?.country_code;
    if (!donotFetchStateAndProvinces && newCountryCode && !stateData?.[newCountryCode]) {
      getStatesOrProvinces(newCountryCode);
    }
  }, [selectedCountry]);
};

/**
 * Helper function that parses a pre-defined issuing authority into an option.
 * We use the shape of country subdivisions because we default to them in case
 * a pre-defined issuing authority is not set for a given country.
 */
const getIssuingAuthorityAsOption = (option) =>
  option ? [{ country_subdivision_code: option, country_subdivision_name: option }] : null;

/**
 * Using the country object as basis, verifies which fields are required to show.
 */
const getCountryFieldConfig = (selectedCountryData, statesOrProvinces) => {
  const config = {
    shouldShowIssueDate: false,
    shouldShowExpirationDate: false,
    shouldShowBothIssueAndExpirationDate: false,
    shouldShowSubdivisionField: false,
    shouldShowIssuingAuthorityField: false,
    issuingAuthorities: null,
  };

  const { country_code, country_content = {} } = selectedCountryData || {};
  const { license_issue_date, license_expiry_date, drive_type_filter } = country_content;

  // we either show only the mandatory license date fieldset, or both of them if they're optional
  if (license_issue_date === FIELD_STATUS_MANDATORY) {
    config.shouldShowIssueDate = true;
  }
  if (license_expiry_date === FIELD_STATUS_MANDATORY) {
    config.shouldShowExpirationDate = true;
  }
  if (license_issue_date === FIELD_STATUS_OPTIONAL && license_expiry_date === FIELD_STATUS_OPTIONAL) {
    config.shouldShowIssueDate = true;
    config.shouldShowExpirationDate = true;
    config.shouldShowBothIssueAndExpirationDate = true;
  }

  config.driveTypeFilter = drive_type_filter;
  // country can be subdivision enabled as long as it has subdivision data
  const countrySubdivisions = statesOrProvinces?.[country_code];
  config.shouldShowSubdivisionField = !!countrySubdivisions?.length;

  // only pre-defined countries should enable issuing authority; also makes sure there is data
  // for them, either pre-defined or from country-subdivisions.
  const isIssuingAuthorityRequiredByCountry = ISSUING_AUTHORITY_FIELD_COUNTRIES.includes(country_code);
  const hasIssuingAuthorityData = ISSUING_AUTHORITIES_BY_COUNTRY[country_code] || countrySubdivisions;
  if (isIssuingAuthorityRequiredByCountry && hasIssuingAuthorityData) {
    config.shouldShowIssuingAuthorityField = true;
    // some of the countries that do have a specific pre-defined issuing authority as well
    config.issuingAuthorities =
      getIssuingAuthorityAsOption(ISSUING_AUTHORITIES_BY_COUNTRY[country_code]) || countrySubdivisions;
  }

  return config;
};

/**
 * This custom hook handles country selection logic.
 * First, it makes sure countries are loaded from GBO, as this is basic for the country fields to operate.
 * Second, it makes sure subdivisions are loaded from GBO after a country selection is made.
 * Third, it checks for country-specific field usage, accounting for specific Alamo and GBO rules.
 *
 * @param {string} initialCountryCode - Country code already selected in the user profile OR defaults to domain country code
 * @return {object} Object with the following properties:
 *  - {object} selectedCountry - Object containing all of the selected country data (code, name, etc)
 *  - {function} setSelectedCountry - Object containing all of the selected country data (code, name, etc)
 *  - {object} countryConfig - Object containing all of the selected country data (code, name, etc)
 *  - {array<object>} countries - Array of countries' data as objects
 *  - {object} statesOrProvinces - Object with each countries' subdivision data lazily fetched from GBO
 */
const useCountryFieldsConfig = (initialCountryCode, donotFetchStateAndProvinces = false, loadingOverlay = true) => {
  // Initial setup needed - we don't set selected country here because we want it to be the country data object
  // so we can have all the information needed accross the hooks.
  const [selectedCountry, setSelectedCountry] = useState(null);

  // We get the countries and state/provinces data from the selectors to pass to the component.
  const countries = useSelector(countriesSelector);
  const statesOrProvinces = useSelector(statesOrProvincesSelector);

  // Prepare the actions used by this hook
  const { getCountries, getStatesOrProvinces } = useActions(locationActions);

  // Gets the country config based on the selected country and its' state/province data.
  const countryConfig = getCountryFieldConfig(selectedCountry, statesOrProvinces);

  // Complimentary custom hooks (not exposed) to fetch data if not present
  useLoadCountriesOnMount(countries, getCountries, loadingOverlay);
  useLoadSubdivisionsOnCountrySelected(
    selectedCountry,
    statesOrProvinces,
    getStatesOrProvinces,
    donotFetchStateAndProvinces
  );

  // Finally, with everything setup, pre-select the initial country code.
  usePreSelectInitialCountry(initialCountryCode, selectedCountry, setSelectedCountry, countries);

  return { selectedCountry, setSelectedCountry, countryConfig, countries, statesOrProvinces };
};

export default useCountryFieldsConfig;
